import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import type { BadgeIntent } from './Badge.js';

/**
 * IconChip — an icon inside a soft rounded square.
 *
 * A single motif that recurs across both references in at least six places:
 * the label of every stat tile, the header of the GST summary and product
 * sheet, the savings card, empty states, and the nodes of the claim-journey
 * stepper. Building it once is the difference between a design system and a
 * folder of similar-looking components.
 *
 * The icon inherits `currentColor` from the chip, so intent is set in one place
 * and the icon follows without being told.
 */

export type IconChipSize = 'sm' | 'md' | 'lg';

const SIZE: Record<IconChipSize, string> = {
  sm: 'size-(--iconchip-size-sm)',
  md: 'size-(--iconchip-size-md)',
  lg: 'size-(--iconchip-size-lg)',
};

/** Icon sizes chosen so the glyph occupies roughly half the chip at every step. */
const ICON_PX: Record<IconChipSize, number> = { sm: 13, md: 16, lg: 20 };

const INTENT: Record<BadgeIntent, string> = {
  neutral: 'bg-neutral-bg text-neutral-fg',
  info: 'bg-info-bg text-info-fg',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
};

export function IconChip({
  icon: IconComponent,
  size = 'md',
  intent = 'neutral',
  className,
}: {
  /** The icon component itself, not an element — the chip decides its size. */
  icon: (props: { size?: number }) => ReactNode;
  size?: IconChipSize;
  intent?: BadgeIntent;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-grid shrink-0 place-items-center rounded-(--iconchip-radius)',
        SIZE[size],
        INTENT[intent],
        className,
      )}
      aria-hidden="true"
    >
      <IconComponent size={ICON_PX[size]} />
    </span>
  );
}
